import { ByteBuffer } from 'flatbuffers';
import type { MultiPolygon, Polygon, Position } from 'geojson';

import { closeRing, readFlatBufferFloat32, readFlatBufferString } from './boundary-geometry.util';
import type {
    FieldBoundaryCollection,
    FieldBoundaryFeature,
    FieldBoundaryProperties,
} from './field-boundary.types';
import { applyUnitDerivedProperties } from './field-unit-properties.util';

/** Payload-level metadata from geographic_unit.fbs / GeographicUnitPayload. */
export interface GeographicUnitPayloadMeta {
    file_zoom: number;
    unit_type: string | null;
    unit_id: string | null;
    season_id: string | null;
    crop_id: string | null;
}

const GUID_FIELD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when the buffer matches GeographicUnitPayload (schema) rather than legacy field blobs. */
export function isGeographicUnitPayloadBuffer(bytes: Uint8Array): boolean {
    if (bytes.length < 24) return false;

    const bb = new ByteBuffer(bytes);
    const root = bb.readInt32(0);
    const fieldsVectorOff = bb.__offset(root, 6);
    if (!fieldsVectorOff) return false;

    const fieldsLen = bb.__vector_len(root + fieldsVectorOff);
    if (fieldsLen <= 0) return false;

    const fieldsStart = bb.__vector(root + fieldsVectorOff);
    let fieldPos: number;
    try {
        fieldPos = bb.__indirect(fieldsStart);
    } catch {
        return false;
    }

    const id = readFlatBufferString(bb, fieldPos, 4)?.trim() ?? '';
    if (!GUID_FIELD_ID.test(id)) return false;

    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return readFieldMultiPolygonRings(bb, dv, fieldPos).length > 0;
}

/**
 * Decodes buffers built from geographic_unit.fbs (GeographicUnitPayload).
 * Point.x = longitude, Point.y = latitude; polygon rings are closed for GeoJSON.
 */
export function decodeGeographicUnitPayload(buffer: ArrayBuffer): FieldBoundaryCollection {
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 24) {
        return { type: 'FeatureCollection', features: [] };
    }

    const bb = new ByteBuffer(bytes);
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const root = bb.readInt32(0);

    const zoomOff = bb.__offset(root, 4);
    const meta: GeographicUnitPayloadMeta = {
        file_zoom: zoomOff ? bb.readInt32(root + zoomOff) : 0,
        unit_type: readFlatBufferString(bb, root, 8),
        unit_id: readFlatBufferString(bb, root, 10),
        season_id: readFlatBufferString(bb, root, 12),
        crop_id: readFlatBufferString(bb, root, 14),
    };

    const fieldsVectorOff = bb.__offset(root, 6);
    if (!fieldsVectorOff) {
        return { type: 'FeatureCollection', features: [] };
    }

    const fieldsLen = bb.__vector_len(root + fieldsVectorOff);
    const fieldsStart = bb.__vector(root + fieldsVectorOff);
    const features: FieldBoundaryFeature[] = [];

    for (let i = 0; i < fieldsLen; i++) {
        const fieldPos = bb.__indirect(fieldsStart + i * 4);
        const fieldId = readFlatBufferString(bb, fieldPos, 4)?.trim();
        if (!fieldId) continue;

        const area = readFlatBufferFloat32(bb, fieldPos, 8) ?? 0;
        const rings = readFieldMultiPolygonRings(bb, dv, fieldPos);
        if (rings.length === 0) continue;

        const geometry = ringsToGeoJsonGeometry(rings);
        if (!geometry) continue;

        const properties: FieldBoundaryProperties = {
            field_id: fieldId,
            field_area: area,
            file_zoom: meta.file_zoom,
            unit_type: meta.unit_type ?? undefined,
            unit_id: meta.unit_id ?? undefined,
            season_id: meta.season_id ?? undefined,
            crop_id: meta.crop_id ?? undefined,
        };
        applyUnitDerivedProperties(properties);

        features.push({
            type: 'Feature',
            properties,
            geometry,
        });
    }

    return { type: 'FeatureCollection', features };
}

function readFieldMultiPolygonRings(
    bb: ByteBuffer,
    dv: DataView,
    fieldPos: number,
): [number, number][][] {
    const geometryOff = bb.__offset(fieldPos, 6);
    if (!geometryOff) return [];

    const geometryField = fieldPos + geometryOff;
    const rel = dv.getInt32(geometryField, true);
    const multiTable = geometryField + rel;

    const polygonsOff = bb.__offset(multiTable, 4);
    if (!polygonsOff) return [];

    const polygonsField = multiTable + polygonsOff;
    const polygonCount = bb.__vector_len(polygonsField);
    const polygonsStart = bb.__vector(polygonsField);

    const rings: [number, number][][] = [];
    for (let p = 0; p < polygonCount; p++) {
        const polygonPos = bb.__indirect(polygonsStart + p * 4);
        const pointsOff = bb.__offset(polygonPos, 4);
        if (!pointsOff) continue;

        const pointsField = polygonPos + pointsOff;
        const pointCount = bb.__vector_len(pointsField);
        const pointsStart = bb.__vector(pointsField);

        const ring: [number, number][] = [];
        for (let j = 0; j < pointCount; j++) {
            const ptAddr = pointsStart + j * 8;
            ring.push([dv.getFloat32(ptAddr, true), dv.getFloat32(ptAddr + 4, true)]);
        }
        if (ring.length >= 3) {
            rings.push(ring);
        }
    }
    return rings;
}

function ringsToGeoJsonGeometry(
    rings: [number, number][][],
): Polygon | MultiPolygon | null {
    if (rings.length === 0) return null;

    const closedPolygons = rings.map((ring) => [closeRing(ring)] as Position[][]);

    if (closedPolygons.length === 1) {
        return { type: 'Polygon', coordinates: closedPolygons[0]! };
    }
    return { type: 'MultiPolygon', coordinates: closedPolygons };
}
